import React from 'react';

// Progress indicators (handoff bundle's feedback/ProgressBar + ProgressRing).
// Both animate on the slow curves from the spec (320ms bar, 480ms ring); the
// global prefers-reduced-motion rule collapses the transitions.

type BarTone = 'primary' | 'accent' | 'success' | 'danger';

const BAR_TONE: Record<BarTone, string> = {
  primary: 'bg-blue-600',
  accent: 'bg-marker-deep',
  success: 'bg-emerald-600',
  danger: 'bg-red-500',
};

export interface ProgressBarProps {
  value: number;
  max?: number;
  tone?: BarTone;
  size?: 'sm' | 'md' | 'lg';
  /** Label rendered above the track. */
  label?: string;
  /** Right-aligned value text ("7/12" or a percent). */
  valueText?: string;
  className?: string;
}

const BAR_SIZE = { sm: 'h-1.5', md: 'h-2.5', lg: 'h-3.5' } as const;

export function ProgressBar({
  value,
  max = 100,
  tone = 'primary',
  size = 'sm',
  label,
  valueText,
  className,
}: ProgressBarProps) {
  const pct = max > 0 ? Math.min(100, Math.max(0, (value / max) * 100)) : 0;
  return (
    <div className={className}>
      {(label || valueText) && (
        <div className="mb-1 flex items-baseline justify-between gap-2">
          {label && <span className="text-xs font-medium text-slate-700">{label}</span>}
          {valueText && (
            <span className="font-mono text-[10px] tabular-nums text-slate-500">{valueText}</span>
          )}
        </div>
      )}
      <div
        role="progressbar"
        aria-valuenow={Math.round(value)}
        aria-valuemin={0}
        aria-valuemax={max}
        className={`overflow-hidden rounded-full bg-paper-sunken ${BAR_SIZE[size]}`}
      >
        <div
          className={`h-full rounded-full transition-[width] duration-300 ease-out ${BAR_TONE[tone]}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

export interface ProgressRingProps {
  value: number;
  max?: number;
  /** Diameter in px. */
  size?: number;
  /** Stroke width in px. */
  thickness?: number;
  tone?: 'primary' | 'accent' | 'success';
  /** Uppercase mono caption under the center value. */
  label?: string;
  /** Center value text; defaults to a rounded percentage. */
  valueText?: string;
  /** Replace the center content entirely. */
  children?: React.ReactNode;
  className?: string;
}

const RING_TONE = {
  primary: 'stroke-blue-600',
  accent: 'stroke-marker-deep',
  success: 'stroke-emerald-600',
} as const;

export function ProgressRing({
  value,
  max = 100,
  size = 72,
  thickness = 8,
  tone = 'primary',
  label,
  valueText,
  children,
  className,
}: ProgressRingProps) {
  const pct = max > 0 ? Math.min(1, Math.max(0, value / max)) : 0;
  const r = (size - thickness) / 2;
  const c = 2 * Math.PI * r;
  return (
    <div
      role="progressbar"
      aria-valuenow={Math.round(value)}
      aria-valuemin={0}
      aria-valuemax={max}
      className={`relative inline-flex items-center justify-center ${className ?? ''}`}
      style={{ width: size, height: size }}
    >
      <svg width={size} height={size} className="-rotate-90" aria-hidden="true">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={thickness}
          className="stroke-paper-sunken"
        />
        <circle
          cx={size / 2}
          cy={size / 2}
          r={r}
          fill="none"
          strokeWidth={thickness}
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={c * (1 - pct)}
          className={`transition-[stroke-dashoffset] duration-500 ease-out ${RING_TONE[tone]}`}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center leading-none">
        {children ?? (
          <>
            <span
              className="font-display font-bold tabular-nums text-slate-900"
              style={{ fontSize: Math.max(11, Math.round(size * 0.27)) }}
            >
              {valueText ?? `${Math.round(pct * 100)}%`}
            </span>
            {label && (
              <span className="mt-0.5 font-mono text-[9px] uppercase tracking-wide text-slate-500">
                {label}
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}
